import { NavBarGroups, RouteNames } from '@/router/names';
import type { Feature } from '@/types';

export type Route = {
    path: string;
    name: string;
    component: () => Promise<typeof import('*.vue')>;
    meta: {
        title: string;
        description?: string;
        navbarGroup?: string;
        public?: boolean;
        admin?: boolean;
        // Plan feature required to enter this route (MILO-282); absent = ungated.
        feature?: Feature;
        // Keep the current view instance when only query parameters change.
        preserveViewOnQueryChange?: boolean;
    };
};

export const routes: Route[] = [
    {
        path: '/admin/support',
        name: RouteNames.AdminSupportInbox,
        component: () => import('@/views/Admin/SupportInbox.vue'),
        meta: { title: 'support.inbox.title', admin: true, preserveViewOnQueryChange: true },
    },
    {
        path: '/admin/support/conversations/:conversationId',
        name: RouteNames.AdminSupportConversation,
        component: () => import('@/views/Admin/SupportConversation.vue'),
        meta: { title: 'support.transcriptTitle', admin: true },
    },
    {
        path: '/',
        name: RouteNames.Home,
        component: () => import('@/views/Home.vue'),
        meta: {
            title: 'pages.home',
        },
    },
    {
        path: '/admin',
        name: RouteNames.AdminHome,
        component: () => import('@/views/Admin/Home.vue'),
        meta: {
            title: 'pages.home',
            admin: true,
        },
    },
    {
        path: '/admin/support/feedback',
        name: RouteNames.AdminSupportFeedback,
        component: () => import('@/views/Admin/SupportInbox.vue'),
        meta: {
            title: 'pages.adminSupportFeedback',
            admin: true,
            preserveViewOnQueryChange: true,
        },
    },
    {
        path: '/admin/support/handoffs',
        name: RouteNames.AdminSupportHandoffs,
        component: () => import('@/views/Admin/SupportInbox.vue'),
        meta: {
            title: 'pages.adminSupportHandoffs',
            admin: true,
            preserveViewOnQueryChange: true,
        },
    },
    {
        path: '/admin/ai-usage',
        name: RouteNames.AdminAiUsage,
        component: () => import('@/views/Admin/AiUsage.vue'),
        meta: {
            title: 'pages.adminAiUsage',
            admin: true,
        },
    },
    {
        path: '/admin/ai-usage/users/:userId',
        name: RouteNames.AdminAiUsageUser,
        component: () => import('@/views/Admin/AiUserDetail.vue'),
        meta: {
            title: 'pages.adminAiUsage',
            admin: true,
        },
    },
    {
        path: '/admin/subscription-invoices',
        name: RouteNames.AdminSubscriptionInvoices,
        component: () => import('@/views/Admin/SubscriptionInvoices.vue'),
        meta: {
            title: 'pages.adminSubscriptionInvoices',
            admin: true,
        },
    },
    {
        path: '/login',
        name: RouteNames.Login,
        component: () => import('@/views/Auth/SignIn.vue'),
        meta: {
            title: 'pages.login',
            description: 'auth.signInMetaDescription',
            public: true,
        },
    },
    {
        path: '/signup',
        name: RouteNames.SignUp,
        component: () => import('@/views/Auth/SignUp.vue'),
        meta: {
            title: 'pages.signUp',
            description: 'auth.signUpMetaDescription',
            public: true,
        },
    },
    {
        path: '/forgot-password',
        name: RouteNames.ForgotPassword,
        component: () => import('@/views/Auth/ForgotPassword.vue'),
        meta: {
            title: 'pages.forgotPassword',
            description: 'auth.forgotPasswordMetaDescription',
            public: true,
        },
    },
    {
        path: '/reset-password',
        name: RouteNames.ResetPassword,
        component: () => import('@/views/Auth/ResetPassword.vue'),
        meta: {
            title: 'pages.resetPassword',
            description: 'auth.resetPasswordMetaDescription',
            public: true,
        },
    },
    // Seat invitations (MILO-178). The path is fixed on the backend side by
    // `app.frontend.auth.invite` in milo-api's config/app.php — the mail builds
    // its link from it, so changing it here alone breaks every pending link.
    {
        path: '/accept-invite',
        name: RouteNames.AcceptInvite,
        component: () => import('@/views/Auth/AcceptInvite.vue'),
        meta: {
            title: 'pages.acceptInvite',
            public: true,
        },
    },
    {
        path: '/verify-email',
        name: RouteNames.VerifyEmail,
        component: () => import('@/views/Auth/VerifyEmail.vue'),
        meta: {
            title: 'pages.verifyEmail',
            description: 'auth.verifyEmailMetaDescription',
            public: true,
        },
    },
    {
        path: '/login/2fa',
        name: RouteNames.TwoFactorVerify,
        component: () => import('@/views/Auth/TwoFactorVerify.vue'),
        meta: {
            title: 'pages.twoFactorVerify',
            public: true,
        },
    },
    {
        path: '/users',
        name: RouteNames.Users,
        component: () => import('@/views/Users/Index.vue'),
        meta: {
            title: 'pages.users',
            navbarGroup: NavBarGroups.Users,
            admin: true,
            preserveViewOnQueryChange: true,
        },
    },
    {
        path: '/users/create',
        name: RouteNames.UsersCreate,
        component: () => import('@/views/Users/Create.vue'),
        meta: {
            title: 'pages.createUser',
            navbarGroup: NavBarGroups.Users,
            admin: true,
        },
    },
    {
        path: '/users/detail/:id?',
        name: RouteNames.UsersDetail,
        component: () => import('@/views/Users/Detail.vue'),
        meta: {
            title: 'pages.userDetails',
            navbarGroup: NavBarGroups.Users,
            admin: true,
        },
    },
    {
        path: '/companies',
        name: RouteNames.Companies,
        component: () => import('@/views/Companies/Index.vue'),
        meta: {
            title: 'pages.companies',
            navbarGroup: NavBarGroups.Companies,
            admin: true,
        },
    },
    {
        path: '/companies/create',
        name: RouteNames.CompanyCreate,
        component: () => import('@/views/Companies/Create.vue'),
        meta: {
            title: 'pages.createCompany',
            navbarGroup: NavBarGroups.Companies,
        },
    },
    {
        path: '/companies/view/:id?',
        name: RouteNames.CompanyView,
        component: () => import('@/views/Companies/View.vue'),
        meta: {
            title: 'pages.companyDetails',
            navbarGroup: NavBarGroups.Companies,
        },
    },
    {
        path: '/companies/detail/:id?',
        name: RouteNames.CompanyDetail,
        component: () => import('@/views/Companies/Detail.vue'),
        meta: {
            title: 'pages.companyDetails',
            navbarGroup: NavBarGroups.Companies,
        },
    },
    {
        path: '/invoices',
        name: RouteNames.Invoices,
        component: () => import('@/views/Invoices/Index.vue'),
        meta: {
            title: 'pages.invoices',
            navbarGroup: NavBarGroups.Invoices,
            preserveViewOnQueryChange: true,
        },
    },
    {
        path: '/invoices/create',
        name: RouteNames.InvoiceCreate,
        component: () => import('@/views/Invoices/Create.vue'),
        meta: {
            title: 'pages.createInvoice',
            navbarGroup: NavBarGroups.Invoices,
        },
    },
    {
        path: '/invoices/view/:id?',
        name: RouteNames.InvoiceView,
        component: () => import('@/views/Invoices/View.vue'),
        meta: {
            title: 'pages.viewInvoice',
            navbarGroup: NavBarGroups.Invoices,
        },
    },
    {
        path: '/invoices/detail/:id?',
        name: RouteNames.InvoiceDetail,
        component: () => import('@/views/Invoices/Detail.vue'),
        meta: {
            title: 'pages.invoiceDetails',
            navbarGroup: NavBarGroups.Invoices,
        },
    },
    {
        path: '/invoices/public/:id?',
        name: RouteNames.InvoicePublicView,
        component: () => import('@/views/Invoices/Public/View.vue'),
        meta: {
            title: 'pages.viewInvoice',
            public: true,
        },
    },
    // Credit notes reuse the invoice views (they self-adapt on type=credit_note);
    // the paths mirror the milo-api frontend URL config (MILO-316).
    {
        path: '/credit-notes/view/:id?',
        name: RouteNames.CreditNoteView,
        component: () => import('@/views/Invoices/View.vue'),
        meta: {
            title: 'pages.viewCreditNote',
            navbarGroup: NavBarGroups.Invoices,
        },
    },
    {
        path: '/credit-notes/public/:id?',
        name: RouteNames.CreditNotePublicView,
        component: () => import('@/views/Invoices/Public/View.vue'),
        meta: {
            title: 'pages.viewCreditNote',
            public: true,
        },
    },
    {
        path: '/quotes',
        name: RouteNames.Quotes,
        component: () => import('@/views/Quotes/Index.vue'),
        meta: {
            title: 'pages.quotes',
            navbarGroup: NavBarGroups.Quotes,
            preserveViewOnQueryChange: true,
        },
    },
    {
        path: '/quotes/create',
        name: RouteNames.QuoteCreate,
        component: () => import('@/views/Quotes/Create.vue'),
        meta: {
            title: 'pages.createInvoice',
            navbarGroup: NavBarGroups.Quotes,
        },
    },
    {
        path: '/quotes/view/:id?',
        name: RouteNames.QuoteView,
        component: () => import('@/views/Quotes/View.vue'),
        meta: {
            title: 'pages.quoteDetails',
            navbarGroup: NavBarGroups.Quotes,
        },
    },
    {
        path: '/quotes/detail/:id?',
        name: RouteNames.QuoteDetail,
        component: () => import('@/views/Quotes/Detail.vue'),
        meta: {
            title: 'pages.quoteDetails',
            navbarGroup: NavBarGroups.Quotes,
        },
    },
    {
        path: '/quotes/public/:id?',
        name: RouteNames.QuotePublicView,
        component: () => import('@/views/Quotes/Public/View.vue'),
        meta: {
            title: 'pages.viewQuote',
            public: true,
        },
    },
    {
        path: '/customers',
        name: RouteNames.Customers,
        component: () => import('@/views/Customers/Index.vue'),
        meta: {
            title: 'pages.customers',
            navbarGroup: NavBarGroups.Customers,
            preserveViewOnQueryChange: true,
        },
    },
    {
        path: '/customers/create',
        name: RouteNames.CustomerCreate,
        component: () => import('@/views/Customers/Create.vue'),
        meta: {
            title: 'customers.createCustomer',
            navbarGroup: NavBarGroups.Customers,
        },
    },
    {
        path: '/customers/view/:id?',
        name: RouteNames.CustomerView,
        component: () => import('@/views/Customers/View.vue'),
        meta: {
            title: 'customers.customerDetails',
            navbarGroup: NavBarGroups.Customers,
        },
    },
    {
        path: '/customers/detail/:id?',
        name: RouteNames.CustomerDetail,
        component: () => import('@/views/Customers/Detail.vue'),
        meta: {
            title: 'customers.customerDetails',
            navbarGroup: NavBarGroups.Customers,
        },
    },
    {
        path: '/config',
        name: RouteNames.Config,
        component: () => import('@/views/Config/Index.vue'),
        meta: {
            title: 'pages.config',
            navbarGroup: NavBarGroups.Users,
        },
    },
    {
        path: '/config/create',
        name: RouteNames.ConfigCreate,
        component: () => import('@/views/Config/Create.vue'),
        meta: {
            title: 'pages.createConfig',
            navbarGroup: NavBarGroups.Users,
        },
    },
    {
        path: '/config/detail/:id?',
        name: RouteNames.ConfigDetail,
        component: () => import('@/views/Config/Detail.vue'),
        meta: {
            title: 'pages.configDetails',
            navbarGroup: NavBarGroups.Users,
        },
    },
    {
        path: '/profile',
        name: RouteNames.Profile,
        component: () => import('@/views/Users/Profile.vue'),
        meta: {
            title: 'pages.profile',
            navbarGroup: NavBarGroups.Users,
        },
    },
    {
        path: '/settings',
        name: RouteNames.Settings,
        component: () => import('@/views/Settings/General.vue'),
        meta: {
            title: 'pages.settingsGeneral',
            navbarGroup: NavBarGroups.Settings,
        },
    },
    {
        path: '/settings/general',
        name: RouteNames.SettingsGeneral,
        component: () => import('@/views/Settings/General.vue'),
        meta: {
            title: 'pages.settingsGeneral',
            navbarGroup: NavBarGroups.Settings,
        },
    },
    {
        path: '/settings/payments',
        name: RouteNames.SettingsPayments,
        component: () => import('@/views/Settings/General.vue'),
        meta: {
            title: 'pages.settingsGeneral',
            navbarGroup: NavBarGroups.Settings,
        },
    },
    {
        path: '/settings/company/create',
        name: RouteNames.SettingsCompanyCreate,
        component: () => import('@/views/Settings/CompanyCreate.vue'),
        meta: {
            title: 'pages.settingsCompany',
            navbarGroup: NavBarGroups.Settings,
        },
    },
    {
        path: '/settings/company/detail/:id?',
        name: RouteNames.SettingsCompanyDetail,
        component: () => import('@/views/Settings/CompanyDetail.vue'),
        meta: {
            title: 'pages.settingsCompany',
            navbarGroup: NavBarGroups.Settings,
        },
    },
    {
        path: '/settings/company/view/:id?',
        name: RouteNames.SettingsCompanyView,
        component: () => import('@/views/Settings/CompanyView.vue'),
        meta: {
            title: 'pages.settingsCompany',
            navbarGroup: NavBarGroups.Settings,
        },
    },
    {
        path: '/settings/company',
        name: RouteNames.SettingsCompany,
        component: () => import('@/views/Settings/Companies.vue'),
        meta: {
            title: 'pages.settingsCompany',
            navbarGroup: NavBarGroups.Settings,
        },
    },
    {
        path: '/settings/users',
        name: RouteNames.SettingsUsers,
        component: () => import('@/views/Settings/Users.vue'),
        meta: {
            title: 'pages.settingsUsers',
            navbarGroup: NavBarGroups.Settings,
        },
    },
    {
        path: '/settings/branding',
        name: RouteNames.SettingsBranding,
        component: () => import('@/views/Settings/Branding.vue'),
        meta: {
            title: 'pages.settingsBranding',
            navbarGroup: NavBarGroups.Settings,
        },
    },
    {
        path: '/settings/api-tokens',
        name: RouteNames.SettingsApiTokens,
        component: () => import('@/views/Settings/ApiTokens.vue'),
        meta: {
            title: 'pages.settingsApiTokens',
            navbarGroup: NavBarGroups.Settings,
            admin: true,
        },
    },
    {
        path: '/connect/telegram',
        name: RouteNames.ConnectTelegram,
        component: () => import('@/views/Connect/Index.vue'),
        meta: {
            title: 'pages.connectTelegram',
            public: true,
        },
    },
    {
        // Shares the generic connect view; the platform is derived from the route name.
        path: '/connect/whatsapp',
        name: RouteNames.ConnectWhatsApp,
        component: () => import('@/views/Connect/Index.vue'),
        meta: {
            title: 'pages.connectWhatsApp',
            public: true,
        },
    },
    {
        // Shares the generic connect view; the platform is derived from the route name.
        path: '/connect/signal',
        name: RouteNames.ConnectSignal,
        component: () => import('@/views/Connect/Index.vue'),
        meta: {
            title: 'pages.connectSignal',
            public: true,
        },
    },
    {
        path: '/catalog',
        name: RouteNames.Catalog,
        component: () => import('@/views/Catalog/Index.vue'),
        meta: {
            title: 'catalog.catalogs',
            navbarGroup: NavBarGroups.Invoices,
        },
    },
    {
        path: '/customer-groups',
        name: RouteNames.CustomerGroups,
        component: () => import('@/views/CustomerGroups/Index.vue'),
        meta: {
            title: 'customerGroups.customerGroups',
            navbarGroup: NavBarGroups.Customers,
            feature: 'customer_groups',
        },
    },
    {
        path: '/customer-groups/create',
        name: RouteNames.CustomerGroupCreate,
        component: () => import('@/views/CustomerGroups/Create.vue'),
        meta: {
            title: 'customerGroups.createCustomerGroup',
            navbarGroup: NavBarGroups.Customers,
            feature: 'customer_groups',
        },
    },
    {
        path: '/customer-groups/detail/:id',
        name: RouteNames.CustomerGroupDetail,
        component: () => import('@/views/CustomerGroups/Detail.vue'),
        meta: {
            title: 'customerGroups.editCustomerGroup',
            navbarGroup: NavBarGroups.Customers,
            feature: 'customer_groups',
        },
    },
    {
        path: '/projects',
        name: RouteNames.Projects,
        component: () => import('@/views/Projects/Index.vue'),
        meta: {
            title: 'projects.projects',
            navbarGroup: NavBarGroups.Customers,
        },
    },
    {
        path: '/projects/create',
        name: RouteNames.ProjectCreate,
        component: () => import('@/views/Projects/Create.vue'),
        meta: {
            title: 'projects.createProject',
            navbarGroup: NavBarGroups.Customers,
        },
    },
    {
        path: '/projects/detail/:id',
        name: RouteNames.ProjectDetail,
        component: () => import('@/views/Projects/Detail.vue'),
        meta: {
            title: 'projects.editProject',
            navbarGroup: NavBarGroups.Customers,
        },
    },
    {
        path: '/time-entries',
        name: RouteNames.TimeEntries,
        component: () => import('@/views/TimeEntries/Index.vue'),
        meta: {
            title: 'timeEntries.timeEntries',
            navbarGroup: NavBarGroups.Invoices,
        },
    },
    {
        path: '/time-entries/reports',
        name: RouteNames.TimeEntryReports,
        component: () => import('@/views/TimeEntries/Reports.vue'),
        meta: {
            title: 'timeEntries.reports.title',
            navbarGroup: NavBarGroups.Invoices,
            // The backend gates the report endpoint (Professional/trial only),
            // so the router guard + PlanGateModal upsell instead of letting the
            // page load and 403 (MILO-310).
            feature: 'time_entry_reports',
        },
    },
    {
        path: '/time-entries/create',
        name: RouteNames.TimeEntryCreate,
        component: () => import('@/views/TimeEntries/Create.vue'),
        meta: {
            title: 'timeEntries.logHours',
            navbarGroup: NavBarGroups.Invoices,
        },
    },
    {
        path: '/time-entries/detail/:id',
        name: RouteNames.TimeEntryDetail,
        component: () => import('@/views/TimeEntries/Detail.vue'),
        meta: {
            title: 'timeEntries.editEntry',
            navbarGroup: NavBarGroups.Invoices,
        },
    },
    {
        path: '/recurring-invoices',
        name: RouteNames.RecurringInvoices,
        component: () => import('@/views/RecurringInvoices/Index.vue'),
        meta: {
            title: 'pages.recurringInvoices',
            navbarGroup: NavBarGroups.Invoices,
            feature: 'recurring_invoices',
        },
    },
    {
        path: '/recurring-invoices/create',
        name: RouteNames.RecurringInvoiceCreate,
        component: () => import('@/views/RecurringInvoices/Create.vue'),
        meta: {
            title: 'pages.createRecurringInvoice',
            navbarGroup: NavBarGroups.Invoices,
            feature: 'recurring_invoices',
        },
    },
    {
        path: '/recurring-invoices/view/:id',
        name: RouteNames.RecurringInvoiceDetail,
        component: () => import('@/views/RecurringInvoices/View.vue'),
        meta: {
            title: 'pages.recurringInvoiceDetails',
            navbarGroup: NavBarGroups.Invoices,
            feature: 'recurring_invoices',
        },
    },
    {
        path: '/recurring-invoices/detail/:id',
        name: RouteNames.RecurringInvoiceEdit,
        component: () => import('@/views/RecurringInvoices/Detail.vue'),
        meta: {
            title: 'pages.editRecurringInvoice',
            navbarGroup: NavBarGroups.Invoices,
            feature: 'recurring_invoices',
        },
    },
    {
        path: '/billing',
        name: RouteNames.Billing,
        component: () => import('@/views/Billing/Billing.vue'),
        meta: {
            title: 'pages.billing',
            navbarGroup: NavBarGroups.Settings,
            // The subscription/invoices tabs sync to ?tab=. Without this every tab
            // click remounts the page: both billing GETs fire again, the expanded
            // plan picker snaps shut, and the invoices refetch on each visit.
            preserveViewOnQueryChange: true,
        },
    },
    {
        path: '/billing/return',
        name: RouteNames.BillingReturn,
        component: () => import('@/views/Billing/BillingReturn.vue'),
        meta: {
            title: 'pages.billingReturn',
            navbarGroup: NavBarGroups.Settings,
        },
    },
    // Public payment return page (MILO-322). Mollie redirects the buyer here (via
    // the milomoves.nl/redirects/payment forwarder) with ?invoice={token}.
    {
        path: '/payment/status',
        name: RouteNames.PaymentStatus,
        component: () => import('@/views/Payments/Status.vue'),
        meta: {
            title: 'pages.paymentStatus',
            public: true,
        },
    },
    {
        path: '/server-error',
        name: RouteNames.ServerError,
        component: () => import('@/views/Errors/ServerError.vue'),
        meta: {
            title: 'pages.serverError',
            public: true,
        },
    },
    // Public so a logged-out visitor sees the 404 instead of the login screen;
    // the view itself renders inside the app shell when there is a session.
    {
        path: '/:catchAll(.*)',
        name: RouteNames.NotFound,
        component: () => import('@/views/Errors/NotFound.vue'),
        meta: {
            title: 'pages.notFound',
            public: true,
        },
    },
];
